<template>
  <div class="line-items-view">
    <div class="line-items-view__container">
      <div class="flex-container">
        <div class="line-items-view__action select">
          <span>Filter By Plant</span>
          <MultiSelect
            class="line-items-view__select"
            :items="plants"
            v-model:value="selectedPlants"
            :auto-close="false"
            placeholder="Select Plants"
            itemsName="Plants"
            data-cy="plant-select"
          />
        </div>
        <div class="line-items-view__action dropdown">
          <span>Sort By</span>
          <DropDownList
            class="line-items-view__sort-dropdown"
            :data-items="sortOptions"
            :text-field="'text'"
            v-model="selectedSortOption"
            placeholder="Sort By"
            data-cy="sort-dropdown"
          />
        </div>
      </div>
    </div>
    <div class="line-items-view__items">
      <LineItemCard
        v-for="lineItem in filteredLineItems"
        :key="lineItem.LineItem"
        :lineItem="lineItem"
        :originalView="useOriginalView"
      />
      <Loader
        v-if="loadingLineItems"
        class="line-items-view__loader"
        size="large"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import LineItemCard from "./LineItemCard.vue";
import { Loader } from "@progress/kendo-vue-indicators";
import type { IdAndName } from "@/Domain/IdAndName";
import MultiSelect from "@/shared/ui/inputs/MultiSelect.vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import type { IngredientSubmissionDetail } from "@/Domain/IngredientSubmissionDetail";

const props = defineProps<{
  lineItems: IngredientSubmissionDetail[];
  useOriginalView: boolean;
  loadingLineItems: boolean;
}>();

const selectedPlants = ref<IdAndName[]>([]);

const sortOptions = [
  { text: "Line Item", value: "ById" },
  { text: "Ingredient Name", value: "ByName" },
  { text: "Plant", value: "ByPlant" },
];

const selectedSortOption = ref(sortOptions[0]);

const plants = computed(() => {
  return props.lineItems
    .map(
      (li) =>
        ({
          ID: li.id,
          Name: li.PlantName,
        } as IdAndName)
    )
    .filter(
      (value, index, self) =>
        self.findIndex((p) => p.Name == value.Name) === index
    );
});

const filteredLineItems = computed(() => {
  let items = [...(props.lineItems ?? [])];

  if (selectedPlants.value.length > 0) {
    items = items.filter((li) =>
      selectedPlants.value.some((p) => p.Name == li.PlantName)
    );
  }

  if (selectedSortOption.value.value == "ById") {
    items = items.sort((a, b) => (a.LineItem ?? 0) - (b.LineItem ?? 0));
  } else if (selectedSortOption.value.value == "ByName") {
    items = items.sort(
      (a, b) =>
        a.Ingredient.localeCompare(b.Ingredient) ||
        a.PlantName.localeCompare(b.PlantName)
    );
  } else if (selectedSortOption.value.value == "ByPlant") {
    items = items.sort((a, b) => a.PlantName.localeCompare(b.PlantName));
  }

  return items;
});

watchEffect(() => {
  selectedPlants.value = plants.value;
});
</script>

<style scoped lang="scss">
.flex-container {
  display: flex;
  gap: 12px;
  justify-content: space-between;
}
.line-items-view {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: "museo-sans", helvetica, arial, sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  text-size-adjust: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    gap: 16px;
  }

  &__switch-mode {
    margin-left: -4px;
    font-size: 11px;
    color: #999;

    &-items {
      margin-left: 6px;
    }
  }

  &__loader {
    margin: 0 auto;
    width: fit-content;
    display: block;
  }

  &__container {
    border: 1px solid #ddd;
    padding: 8px 15px;
    background: #dedede;
    > header {
      font-weight: 400;
      margin: -10px -15px 10px -15px;
      padding: 8px 15px;
      font-size: 13px;
      background: #dedede;
      border-bottom: 1px solid #e8e8e8;
      display: flex;
      justify-content: space-between;
    }
    > footer {
      padding: 8px 15px;
      font-size: 13px;
      background: #efefef;
      border-top: 1px solid #e8e8e8;
      margin: 10px -15px -8px;
    }

    &.yellow {
      background: #fde9b3;
      color: #666;
      > header {
        background: #f3dfaa;
        border-bottom: 1px solid #e7d4a0;
      }
    }
  }

  &__action {
    font-weight: normal;

    &.select {
      width: 70%;
    }

    &.dropdown {
      width: 30%;
    }
  }

  .k-input-inner {
    font-size: 12px;
  }
}

.k-grid-header {
  background-color: #dedede;
}
</style>
